/**
 * SSR Data Context — fournit les donnees pre-fetchees cote serveur aux composants.
 * En SSR, le serveur fetch les donnees API et les injecte dans ce context.
 * Cote client, les composants useEffect re-fetchent (hydratation).
 */
import { createContext, useContext, useRef } from 'react'

export interface SSRData {
  /** Vitrine servant la page — logo, marque, thème et navigation (multi-sites). */
  site?: {
    key: string
    name: string
    brandLabel: string
    logoUrl: string | null
    theme: Record<string, string>
    nav: { header?: { label: string; href: string }[]; footer?: { title: string; links: { label: string; href: string }[] }[] }
    ui?: Record<string, string>
    defaultLocale: string
    status: string
    isDefault: boolean
  } | null

  /** Bandeau promo de l'en-tête, déjà résolu par l'API (textes + chiffres). */
  promoBanners?: any | null

  // MegaMenu / navigation
  categories?: any[]
  universes?: any[]
  events?: any[]
  blogPosts?: any[]
  activePages?: any[] | null
  blogEnabled?: boolean

  // Footer (partage sur toutes les pages) : top sous-categories par CA + evenements a venir
  footerCategories?: any[]
  upcomingEvents?: any[]

  // Accueil — section avis (selection accueil + note globale reelle)
  homepageReviews?: any[]
  homepageExternalReviews?: any[]
  reviewStats?: { avgRating: number; count: number } | null
  /** Accueil : vitrine d'avis, pages cadeau, visuels du hero (cf. pages/HomePage). */
  showcaseReviews?: any[]
  giftPages?: any[]
  heroProducts?: any[]

  // Page publique d'un tag (`/<slug>`) — préchargée par le SSR pour éviter
  // un aller-retour et pour que le HTML servi porte déjà le contenu.
  tag?: any | null
  tagProducts?: any[]

  // Page-specific
  products?: any[]
  featured?: any[]
  newest?: any[]
  bestsellers?: any[]
  total?: number
  category?: any
  universe?: any
  event?: any
  /** Vignettes « intention d'achat » de la page événement (grille sous le hero). */
  eventIntents?: any[]
  product?: any
  similar?: any[]
  /** Fiche produit : bloc « De la même collection » (nom + cartes), ou null. */
  collection?: { name: string; products: any[] } | null
  sections?: any[]      // HomePage / AtelierPage sections
  themeProducts?: any[]
  themes?: any[]
  facets?: any[]
  blogPost?: any
  blogLinks?: any[]
  /** Compagnons cross-sell d'une fiche produit (sera typé CompanionPublic[] côté client) */
  companions?: any[]
  /** Fiche porteuse d'un kit : dessins à cocher + barème (cf. `PublicKit`). */
  kit?: any
  /** Fêtes datées à venir du produit (cf. `@myfabrik/shared/lib/deliveryEvents`). */
  deliveryEvents?: { slug: string; name: string; date: string }[]
  /** Délai d'expédition annoncé (réglage boutique, cf. `@myfabrik/shared/lib/shippingLeadTime`). */
  shippingLeadTime?: { minDays: number; maxDays: number } | null
  /** Avis natifs SSR : nativeCount du combo + JSON-LD AggregateRating/Review
   *  (le JSON-LD Product est généré côté SSR dans site-ssr/src/server.ts) */
  reviews?: { items: any[]; stats: { avgRating: number; count: number } }
  blogCategories?: any[]
  blogCategory?: any
  totalPages?: number
  contentPage?: any        // ContentPage / SlugPage
  /** Le SSR a resolu une 404 sur une route a entite (collection supprimee, etc.).
   *  Le client s'en sert pour afficher le bloc 404 DES l'hydratation : sans ce
   *  drapeau il rendrait d'abord la coquille de la page (titre = slug, grille vide)
   *  le temps de re-fetcher, ce qui ressemble a une page vide et non a une 404. */
  _notFound?: boolean
}

interface SSRDataContextValue {
  data: SSRData
  initialUrl: string | null
}

const SSRDataContext = createContext<SSRDataContextValue>({ data: {}, initialUrl: null })

export function markHydrationConsumed() {
  // Compat : l'ancien appel depuis Layout ne doit plus couper les donnees SSR.
  // React peut hydrater les boundaries Suspense apres les effets parents ; un flag
  // temporel rendait donc les routes sans ssrData et declenchait React #418.
}

export function SSRDataProvider({ data, children }: { data: SSRData; children: React.ReactNode }) {
  const initialUrlRef = useRef<string | null>(null)
  if (typeof window !== 'undefined' && initialUrlRef.current === null) {
    initialUrlRef.current = window.location.pathname + window.location.search
  }
  return (
    <SSRDataContext.Provider value={{ data, initialUrl: initialUrlRef.current }}>
      {children}
    </SSRDataContext.Provider>
  )
}

export function useSSRData(): SSRData {
  const ctx = useContext(SSRDataContext)
  // SSR : toujours utiliser les donnees du contexte
  if (typeof window === 'undefined') return ctx.data

  // Client : conserver le snapshot SSR pour l'URL hydratee, meme si React hydrate
  // une boundary Suspense apres les effets parents. Sur navigation SPA, l'URL
  // change et les composants doivent re-fetch leurs propres donnees.
  const currentUrl = window.location.pathname + window.location.search
  return ctx.initialUrl === currentUrl ? ctx.data : {}
}
